import { Token, TokenType, Tokenizer } from './tokenizer'
import {
  ASTNode,
  ProgramNode,
  Statement,
  Expression,
  ForLoopNode,
  WhileLoopNode,
  DoWhileLoopNode,
  BlockStatement,
  DeclarationStatement,
  AssignmentStatement,
  CompoundAssignmentStatement,
  ExpressionStatement,
  UpdateExpression,
  BinaryExpression,
  UnaryExpression,
  Identifier,
  Literal,
  ParseError,
} from '../types'

export class Parser {
  private tokens: Token[]
  private current: number = 0
  private errors: ParseError[] = []

  constructor(input: string) {
    const tokenizer = new Tokenizer(input)
    this.tokens = tokenizer.tokenize()
  }

  parse(): ProgramNode {
    const statements: Statement[] = []

    while (!this.isAtEnd()) {
      try {
        const stmt = this.statement()
        if (stmt) {
          statements.push(stmt)
        }
      } catch (error) {
        // エラーをスキップして次のステートメントから回復を試みる
        this.synchronize()
      }
    }

    return {
      type: 'program',
      body: statements,
      line: 1,
      column: 1,
    }
  }

  getErrors(): ParseError[] {
    return this.errors
  }

  private statement(): Statement | null {
    // for文
    if (this.match(TokenType.FOR)) {
      return this.forStatement()
    }

    // while文
    if (this.match(TokenType.WHILE)) {
      return this.whileStatement()
    }

    // do-while文
    if (this.match(TokenType.DO)) {
      return this.doWhileStatement()
    }

    // 変数宣言
    if (this.match(TokenType.INT, TokenType.FLOAT, TokenType.CHAR)) {
      return this.declarationStatement()
    }

    // ブロック文
    if (this.check(TokenType.LBRACE)) {
      return this.blockStatement()
    }

    // その他の式文
    return this.expressionStatement()
  }

  private forStatement(): ForLoopNode {
    const startToken = this.previous()
    this.consume(TokenType.LPAREN, '( が必要です')

    // 初期化部
    let init: Statement | undefined
    if (!this.check(TokenType.SEMICOLON)) {
      if (this.match(TokenType.INT, TokenType.FLOAT, TokenType.CHAR)) {
        // forループ内の変数宣言では、declarationStatementWithoutSemicolonを使用
        const declarations = this.declarationStatementWithoutSemicolon()
        // 複数の宣言がある場合は特別な型として扱う
        if (declarations.length === 1) {
          init = declarations[0]
        } else {
          init = {
            type: 'multiple-declaration',
            declarations: declarations,
            line: declarations[0].line,
            column: declarations[0].column,
          } as any
        }
        this.consume(TokenType.SEMICOLON, '; が必要です')
      } else {
        const expr = this.expression()
        if (expr) {
          init = {
            type: 'expression',
            expression: expr,
            line: expr.line,
            column: expr.column,
          } as ExpressionStatement
        }
        this.consume(TokenType.SEMICOLON, '; が必要です')
      }
    } else {
      this.advance() // セミコロンをスキップ
    }

    // 条件部
    let condition: Expression | undefined
    if (!this.check(TokenType.SEMICOLON)) {
      condition = this.expression()
    }
    this.consume(TokenType.SEMICOLON, '; が必要です')

    // 更新部
    let update: Statement | undefined
    if (!this.check(TokenType.RPAREN)) {
      const expr = this.expression()
      if (expr) {
        update = {
          type: 'expression',
          expression: expr,
          line: expr.line,
          column: expr.column,
        } as ExpressionStatement
      }
    }

    this.consume(TokenType.RPAREN, ') が必要です')

    // ループ本体
    const body = this.statement()

    return {
      type: 'for',
      init: init!,
      condition: condition || this.createTrueLiteral(),
      update: update!,
      body: body ? [body] : [],
      line: startToken.line,
      column: startToken.column,
    }
  }

  private whileStatement(): WhileLoopNode {
    const startToken = this.previous()
    this.consume(TokenType.LPAREN, '( が必要です')
    
    const condition = this.expression()
    this.consume(TokenType.RPAREN, ') が必要です')
    
    const body = this.statement()

    return {
      type: 'while',
      condition: condition || this.createTrueLiteral(),
      body: body ? [body] : [],
      line: startToken.line,
      column: startToken.column,
    }
  }

  private doWhileStatement(): DoWhileLoopNode {
    const startToken = this.previous()
    const body = this.statement()
    
    this.consume(TokenType.WHILE, 'while が必要です')
    this.consume(TokenType.LPAREN, '( が必要です')
    
    const condition = this.expression()
    this.consume(TokenType.RPAREN, ') が必要です')
    this.consume(TokenType.SEMICOLON, '; が必要です')

    return {
      type: 'do-while',
      condition: condition || this.createTrueLiteral(),
      body: body ? [body] : [],
      line: startToken.line,
      column: startToken.column,
    }
  }

  private blockStatement(): BlockStatement {
    const startToken = this.peek()
    this.consume(TokenType.LBRACE, '{ が必要です')
    
    const statements: Statement[] = []
    while (!this.check(TokenType.RBRACE) && !this.isAtEnd()) {
      const stmt = this.statement()
      if (stmt) {
        statements.push(stmt)
      }
    }
    
    this.consume(TokenType.RBRACE, '} が必要です')

    return {
      type: 'block',
      body: statements,
      line: startToken.line,
      column: startToken.column,
    }
  }

  private declarationStatement(): Statement {
    const declarations = this.declarationStatementWithoutSemicolon()
    this.consume(TokenType.SEMICOLON, '; が必要です')
    
    // 複数の宣言がある場合は、特別な処理が必要
    // 最初の宣言を返し、残りはstatements配列に追加する
    if (declarations.length === 1) {
      return declarations[0]
    }
    
    // 複数宣言用の特別な型を作成
    return {
      type: 'multiple-declaration',
      declarations: declarations,
      line: declarations[0].line,
      column: declarations[0].column,
    } as any
  }

  private declarationStatementWithoutSemicolon(): DeclarationStatement[] {
    const dataTypeToken = this.previous()
    const dataType = dataTypeToken.value as 'int' | 'float' | 'char'
    const declarations: DeclarationStatement[] = []
    
    // 最初の変数
    do {
      const identifierToken = this.consume(TokenType.IDENTIFIER, '識別子が必要です')
      
      let initialValue: Expression | undefined
      if (this.match(TokenType.ASSIGN)) {
        initialValue = this.expression()
      }

      declarations.push({
        type: 'declaration',
        dataType,
        identifier: identifierToken.value,
        initialValue,
        line: dataTypeToken.line,
        column: dataTypeToken.column,
      })
    } while (this.match(TokenType.COMMA)) // カンマがある限り続ける

    return declarations
  }

  private expressionStatement(): ExpressionStatement | null {
    const expr = this.expression()
    if (!expr) return null
    
    this.consume(TokenType.SEMICOLON, '; が必要です')

    return {
      type: 'expression',
      expression: expr,
      line: expr.line,
      column: expr.column,
    }
  }

  private expression(): Expression | null {
    return this.assignment()
  }

  private assignment(): Expression | null {
    const expr = this.logicalOr()
    if (!expr) return null

    if (this.match(TokenType.ASSIGN)) {
      const value = this.assignment()
      if (expr.type === 'identifier' && value) {
        return {
          type: 'assignment',
          identifier: (expr as Identifier).name,
          value,
          line: expr.line,
          column: expr.column,
        } as any // AssignmentStatementとして扱う
      }
    }

    if (this.match(TokenType.PLUS_ASSIGN, TokenType.MINUS_ASSIGN)) {
      const operator = this.previous().value as '+=' | '-='
      const value = this.assignment()
      if (expr.type === 'identifier' && value) {
        return {
          type: 'compound',
          identifier: (expr as Identifier).name,
          operator,
          value,
          line: expr.line,
          column: expr.column,
        } as any // CompoundAssignmentStatementとして扱う
      }
    }

    return expr
  }

  private logicalOr(): Expression | null {
    let expr = this.logicalAnd()
    if (!expr) return null

    while (this.match(TokenType.OR)) {
      const operator = this.previous()
      const right = this.logicalAnd()
      if (right) {
        expr = {
          type: 'binary',
          operator: operator.value,
          left: expr,
          right,
          line: operator.line,
          column: operator.column,
        } as any
      }
    }

    return expr
  }

  private logicalAnd(): Expression | null {
    let expr = this.equality()
    if (!expr) return null

    while (this.match(TokenType.AND)) {
      const operator = this.previous()
      const right = this.equality()
      if (right) {
        expr = {
          type: 'binary',
          operator: operator.value,
          left: expr,
          right,
          line: operator.line,
          column: operator.column,
        } as any
      }
    }

    return expr
  }

  private equality(): Expression | null {
    let expr = this.comparison()
    if (!expr) return null

    while (this.match(TokenType.EQUAL, TokenType.NOT_EQUAL)) {
      const operator = this.previous()
      const right = this.comparison()
      if (right) {
        expr = {
          type: 'binary',
          operator: operator.value,
          left: expr,
          right,
          line: operator.line,
          column: operator.column,
        } as any
      }
    }

    return expr
  }

  private comparison(): Expression | null {
    let expr = this.addition()
    if (!expr) return null

    while (this.match(TokenType.LESS_THAN, TokenType.GREATER_THAN, TokenType.LESS_EQUAL, TokenType.GREATER_EQUAL)) {
      const operator = this.previous()
      const right = this.addition()
      if (right) {
        expr = {
          type: 'binary',
          operator: operator.value,
          left: expr,
          right,
          line: operator.line,
          column: operator.column,
        } as any
      }
    }

    return expr
  }

  private addition(): Expression | null {
    let expr = this.multiplication()
    if (!expr) return null

    while (this.match(TokenType.PLUS, TokenType.MINUS)) {
      const operator = this.previous()
      const right = this.multiplication()
      if (right) {
        expr = {
          type: 'binary',
          operator: operator.value,
          left: expr,
          right,
          line: operator.line,
          column: operator.column,
        } as any
      }
    }

    return expr
  }

  private multiplication(): Expression | null {
    let expr = this.unary()
    if (!expr) return null

    while (this.match(TokenType.MULTIPLY, TokenType.DIVIDE, TokenType.MODULO)) {
      const operator = this.previous()
      const right = this.unary()
      if (right) {
        expr = {
          type: 'binary',
          operator: operator.value,
          left: expr,
          right,
          line: operator.line,
          column: operator.column,
        } as any
      }
    }

    return expr
  }

  private unary(): Expression | null {
    if (this.match(TokenType.NOT, TokenType.MINUS)) {
      const operator = this.previous()
      const operand = this.unary()
      if (operand) {
        return {
          type: 'unary',
          operator: operator.value,
          operand,
          line: operator.line,
          column: operator.column,
        } as any
      }
    }

    return this.postfix()
  }

  private postfix(): Expression | null {
    let expr = this.primary()
    if (!expr) return null

    if (this.match(TokenType.INCREMENT, TokenType.DECREMENT)) {
      const operator = this.previous()
      if (expr.type === 'identifier') {
        return {
          type: 'update',
          operator: operator.value as '++' | '--',
          prefix: false,
          identifier: (expr as Identifier).name,
          line: operator.line,
          column: operator.column,
        } as any
      }
    }

    return expr
  }

  private primary(): Expression | null {
    // リテラル
    if (this.match(TokenType.NUMBER)) {
      const token = this.previous()
      const value = parseFloat(token.value)
      return {
        type: 'literal',
        value,
        dataType: token.value.includes('.') ? 'float' : 'int',
        line: token.line,
        column: token.column,
      } as any
    }

    if (this.match(TokenType.STRING)) {
      const token = this.previous()
      return {
        type: 'literal',
        value: token.value,
        dataType: 'string',
        line: token.line,
        column: token.column,
      } as any
    }

    if (this.match(TokenType.CHAR_LITERAL)) {
      const token = this.previous()
      return {
        type: 'literal',
        value: token.value,
        dataType: 'char',
        line: token.line,
        column: token.column,
      } as any
    }

    // 識別子
    if (this.match(TokenType.IDENTIFIER)) {
      const token = this.previous()
      return {
        type: 'identifier',
        name: token.value,
        line: token.line,
        column: token.column,
      } as any
    }

    // 前置インクリメント/デクリメント
    if (this.match(TokenType.INCREMENT, TokenType.DECREMENT)) {
      const operator = this.previous()
      const identifier = this.consume(TokenType.IDENTIFIER, '識別子が必要です')
      return {
        type: 'update',
        operator: operator.value as '++' | '--',
        prefix: true,
        identifier: identifier.value,
        line: operator.line,
        column: operator.column,
      } as any
    }

    // 括弧で囲まれた式
    if (this.match(TokenType.LPAREN)) {
      const expr = this.expression()
      this.consume(TokenType.RPAREN, ') が必要です')
      return expr
    }

    return null
  }

  // ヘルパーメソッド
  private match(...types: TokenType[]): boolean {
    for (const type of types) {
      if (this.check(type)) {
        this.advance()
        return true
      }
    }
    return false
  }

  private check(type: TokenType): boolean {
    if (this.isAtEnd()) return false
    return this.peek().type === type
  }

  private advance(): Token {
    if (!this.isAtEnd()) this.current++
    return this.previous()
  }

  private isAtEnd(): boolean {
    return this.peek().type === TokenType.EOF
  }

  private peek(): Token {
    return this.tokens[this.current]
  }

  private previous(): Token {
    return this.tokens[this.current - 1]
  }

  private consume(type: TokenType, message: string): Token {
    if (this.check(type)) return this.advance()

    const token = this.peek()
    this.error(token, message)
    throw new Error(message)
  }

  private error(token: Token, message: string): void {
    this.errors.push({
      type: 'SYNTAX_ERROR',
      message,
      line: token.line,
      column: token.column,
    })
  }

  private synchronize(): void {
    this.advance()

    while (!this.isAtEnd()) {
      if (this.previous().type === TokenType.SEMICOLON) return

      switch (this.peek().type) {
        case TokenType.FOR:
        case TokenType.WHILE:
        case TokenType.DO:
        case TokenType.IF:
        case TokenType.INT:
        case TokenType.FLOAT:
        case TokenType.CHAR:
        case TokenType.RETURN:
          return
      }

      this.advance()
    }
  }

  private createTrueLiteral(): Literal {
    return {
      type: 'literal',
      value: 1,
      dataType: 'int',
      line: this.peek().line,
      column: this.peek().column,
    }
  }
}